import React from 'react';
import { toast } from 'react-toastify';
import { ContactSupport, Dialog, DialogContent, DialogTitle } from '@zawarski/eti-ui';
import { ToastStatus } from '@/common/enum';
import ToastMessage from 'components/primitive/ToastMessage';
import { useAppSelector } from 'store/hooks.ts';
import { selectAuthToken, userIDSelector } from 'store/feature/authToken/authToken.selector.ts';
import { accountGroupNameSelector, accountSelector } from 'store/feature/accountInfo/account.selector.ts';
import { contactSupportSender } from './contactSupportSender.ts';

/**
 * The dashboard API's contact-support endpoint, which emails support. It accepts the Cognito
 * id_token decon signs in with. `VITE_APP_CONTACT_SUPPORT_API` (REP's name) overrides it.
 */
const CONTACT_SUPPORT_API_URL: string = import.meta.env.VITE_APP_CONTACT_SUPPORT_API || 'https://dashboard.cloudeoc.com/prod/api/contact-support';

/** Keeps the dialog inside a phone-width viewport; decon compiles no Tailwind, so no utility classes. */
const contentStyle: React.CSSProperties = { maxHeight: '90vh', overflowY: 'auto' };

interface ModalContactSupportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The side menu's Contact Support form: eti-ui's ContactSupport (subject, message, attachments) in
 * an eti-ui dialog, as REP does. eti-ui sends the token in the Authorization header and renders
 * every failure inside the form, so only a delivered message needs a toast here.
 */
const ModalContactSupport: React.FC<ModalContactSupportProps> = ({ open, onOpenChange }) => {
  const token = useAppSelector(selectAuthToken) || '';
  const userId = useAppSelector(userIDSelector);
  const account = useAppSelector(accountSelector);
  const groupName = useAppSelector(accountGroupNameSelector);
  const sender = contactSupportSender(account, groupName);

  const handleSent = () => {
    toast.info(<ToastMessage status={ToastStatus.SUCCESS} message='Message sent to support.' />);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton style={contentStyle}>
        {/* ContactSupport draws its own heading; the dialog still needs a title for screen readers. */}
        <DialogTitle className='eu:sr-only'>Contact Support</DialogTitle>
        {/* Mounted only while open, so every visit starts a fresh form and attachment group. */}
        {open ? <ContactSupport cognitoToken={token} fileStoreToken={token} fileStoreUserId={userId ?? 0} apiUrl={CONTACT_SUPPORT_API_URL} onSuccess={handleSent} {...sender} /> : null}
      </DialogContent>
    </Dialog>
  );
};

export default ModalContactSupport;
